import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth-store';

/**
 * The profile "Time Format" preference, reduced to the one thing the portal needs.
 *
 * `timeFormat` is a dayjs pattern stored on the personal profile (Display Formats)
 * and on the company profile. It used to drive only content rendered on screens;
 * it now also drives the portal's calendars and pickers (#2067).
 *
 * The important part is what happens when NOTHING is stored. Both levels are
 * nullable — `company.service.ts` only writes `featureData.timeFormat` when an
 * admin picks a preset, and a user who never opened Display Formats has no value.
 * Both FullCalendar and `Intl` already render 24-hour for a German or French
 * portal, so defaulting to `'hh:mm A'` the way DisplayFormatsForm does would
 * force 12-hour onto every European-locale portal — shipping a regression as a
 * fix. So `hour12` is `undefined` when nothing is stored, and callers must then
 * pass no format at all and let locale win.
 */
export type ResolvedTimeFormat = {
  /** `undefined` = nothing stored; do NOT pass a format, let the locale decide. */
  hour12: boolean | undefined;
  /** The raw dayjs pattern, for the few consumers that honour the whole thing. */
  pattern: string | undefined;
};

/**
 * dayjs uppercase `H` → 24-hour clock; lowercase `h` (or neither) → 12-hour.
 *
 * Bracketed segments are dayjs escapes for literal text, so `h:mm A [Hrs]`
 * is a 12-hour pattern even though it contains an `H`. Strip the escapes
 * before testing, or that custom pattern false-positives into 24-hour.
 */
export function is24HourPattern(pattern: string): boolean {
  return /H/.test(pattern.replace(/\[[^\]]*\]/g, ''));
}

/**
 * The FullCalendar props that force a 24-hour axis and 24-hour event chips.
 *
 * Returns an EMPTY object on the 12-hour and the unresolved path, and the
 * caller spreads it — so on those paths the props are genuinely absent and
 * FullCalendar keeps the locale-driven default it uses today. Passing
 * `hour12: true` instead would override a German portal's 24-hour rendering.
 *
 * `hour: '2-digit'` is load-bearing. FullCalendar's slot-label default carries
 * `omitZeroMinute: true`, so flipping only `hour12` renders a bare `13` instead
 * of `13:00`. Both fields have to be stated for the axis to read as a clock.
 *
 * Seconds and the `z` timezone token are deliberately dropped even when the
 * stored pattern has them: they are noise on a calendar axis. The full pattern
 * still reaches DateTimePicker, which knows what to do with it.
 */
export function fullCalendarTimeFormatProps(hour12: boolean | undefined) {
  if (hour12 !== false) return {};
  const clock = { hour: '2-digit', minute: '2-digit', hour12: false } as const;
  return { slotLabelFormat: clock, eventTimeFormat: clock };
}

/** Reads a `timeFormat` out of an untyped settings blob, ignoring blanks. */
function readPattern(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = (raw as Record<string, unknown>).timeFormat;
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Resolution order: the user's own override, then the account default, then
 * nothing. Both values are already in the auth store from ME_QUERY — `me`
 * carries the whole `userSettings` document as an untyped blob, which is why
 * this narrows rather than reads a typed field. No new query, no loading state.
 */
export function useTimeFormat(): ResolvedTimeFormat {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);

  return useMemo(() => {
    const pattern = readPattern(me?.userSettings) ?? readPattern(company?.featureData);
    return {
      pattern,
      hour12: pattern === undefined ? undefined : !is24HourPattern(pattern),
    };
  }, [me?.userSettings, company?.featureData]);
}
